import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { CheckMark } from '../../../../../../assets/images/orders';
import {
  ISerialErrors,
  IpillsByReference,
  IproductItemData,
  IproductReferenceDataInCountry,
  IShipmentData,
  SHIPMENT_STATUS
} from '../../../../../../types/orders';
import ProductInformationCard from '../ProductInformationCard/ProductInformationCard';
import { PRODUCT_CATEGORY } from '../../../../../../types/category';
import { IOrganizationConfig } from '../../../../../../types/product.configuration.types';
import BordOneToneIcon from '../../../../../../components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import StockMetrics from '../StockComponents/StockMetrics/StockMetrics';
import { formatCost } from 'utils/formatNumbers';
import { Switch } from 'antd';
import useStockReservationState from 'state/useStockReservationState';
import { IAvailableStockInfoItem } from '../../../../../../types/stockOrders';
import { IFloatSelectSerialItem } from '../StockComponents/FloatSelectSerial/FloatSelectSerial';
import BulkSelectSerialModal from '../StockComponents/FloatSelectSerial/Components/BulkSelectSerial/BulkSelectSerialModal';
import CancelReservationModal from '../StockComponents/FloatSelectSerial/Components/CancelReservationModal/CancelReservationModal';
import { displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import { NudosSelectButton } from 'components/NudosComponents';
import useOrdersState from 'state/useOrdersState';
import BordAcordeon from '../../../../../../components/BordAcordeon/BordAcordeon';
import { areAllReservedProductsDelivered } from '../../../../../../utils/stockDeliveryValidation';
import useTab2ProductsAccordionState from '../../../../../../state/useTab2ProductsAccordionState';
import './ReferenceCard.scss';
import { BordBadge } from 'bord-design-system';
import { useQuoteItemOriginalAttributes } from 'views/Quote/hooks/useQuoteItemOriginalAttributes';

const ReferenceCard = ({
  reference,
  setHasDataChanged,
  countryId,
  countryCode,
  serialErrorsList,
  changeErrorList,
  organizationName,
  updateListOfProductsToUpdate,
  updateWithoutLoaders,
  organizationId,
  orderId,
  savedOrganizationConfigs,
  orderStatus,
  shimpmentData
}: IReferenceCard) => {
  const { stockProductAvailabilityData } = useStockReservationState();
  const { productsSelected, setProductsSelected } = useOrdersState();
  const { expandedAccordions, setAccordionExpanded } = useTab2ProductsAccordionState();
  const [useSameProvider, setUseSameProvider] = useState(false);
  const [useSameMPN, setUseSameMPN] = useState(false);
  const [itemsList, setItemsList] = useState<IproductItemData[]>([]);
  const [isBulkReservationModalOpen, setIsBulkReservationModalOpen] = useState(false);
  const [isCancelReservationModalOpen, setIsCancelReservationModalOpen] = useState(false);
  const [isReservingAll, setIsReservingAll] = useState(false);

  const { originalAttributes } = useQuoteItemOriginalAttributes(reference?.items?.[0]?.quoteItemId);

  const sku = reference?.sku || '';
  const isAccordionExpanded = expandedAccordions[sku] ?? false;

  const { totalStockInfo, availableStockInfo, reservedStockInfo } =
    stockProductAvailabilityData?.find(item => item.sku === reference.sku) || {};
  const currentStockCount = totalStockInfo?.quantity ?? 0;
  const stockAvailableReservationCount = availableStockInfo?.quantity ?? 0;
  const reservedStockCount = reservedStockInfo?.quantity ?? 0;
  const productName = reference?.productName || '';
  const pills = reference?.pills || {};
  const referencePills = typeof pills === 'string' ? (JSON.parse(pills) as IpillsByReference) : pills;
  const brandImage = reference?.brandImage || '';
  const items: IproductItemData[] = reference?.items || [];
  const itemsQuantity = reference?.quantity || 0;
  const unitPrice = reference?.unitPrice || 0;
  const totalCost = reference?.total || 0;
  const currencyText = 'USD';

  const getShipmentDataByStatus = shimpmentData?.filter(item => item.status === SHIPMENT_STATUS.DELIVERED);
  const getProductsBySku = getShipmentDataByStatus?.flatMap(item =>
    item.products.filter(product => product.sku === sku)
  );

  const filterItemsWithoutStockReservation = itemsList.filter(item => item.stockReservation === null);

  const currentCommonProvider = filterItemsWithoutStockReservation?.[0]?.provider;
  const currentCommonProviderId = filterItemsWithoutStockReservation?.[0]?.providerId;
  const currentCommonMPN = filterItemsWithoutStockReservation?.[0]?.mpn;
  const currentCommonMPNId = filterItemsWithoutStockReservation?.[0]?.mpnId;
  const currentCommonCategory = filterItemsWithoutStockReservation?.[0]?.category;
  const currentCommonBuyingPrice = filterItemsWithoutStockReservation?.[0]?.purchaseValue || '';
  const currentCommonProviderPurchaseOrder = filterItemsWithoutStockReservation?.[0]?.providerPurchaseOrder || '';

  const referenceHasMoreThanOneItem = items?.length > 1;
  const enabledSameProviderAndPricesInAll =
    !!currentCommonProvider || !!currentCommonBuyingPrice || !!currentCommonProviderPurchaseOrder || !!currentCommonMPN;
  const isDepCategory = currentCommonCategory === PRODUCT_CATEGORY.DEP_LAPTOPS;
  const filterItemsWithStockReservation = itemsList.filter(item => item.stockReservation !== null);
  const renderStockProducts = filterItemsWithStockReservation
    .map(item => item.stockReservation?.stockProduct)
    .filter((product): product is IAvailableStockInfoItem => product !== null && product !== undefined);
  const allItemsWithoutStockReservation = filterItemsWithoutStockReservation?.length;

  const allReservedProductsAreDelivered = areAllReservedProductsDelivered(
    renderStockProducts,
    shimpmentData || [],
    sku
  );

  const allItemsIsReserved = itemsList.every(item => item.stockReservation !== null);
  const disabledSwitch = stockAvailableReservationCount === 0 || allReservedProductsAreDelivered;

  const renderEmptyPillsMessage =
    Object.entries(referencePills).filter(
      ([key, val]) => key !== 'Gama' && key !== 'Cargo' && !key.includes('Perfil') && val
    ).length === 0;

  const mapAvailableItemsToFloatSelectSerialItems = (
    items: IAvailableStockInfoItem[] = []
  ): IFloatSelectSerialItem[] => {
    return items.map((item, index) => ({
      providerName: item.providerName,
      serial: item.serial,
      billingNumber: item.invoiceNumber,
      date: item.entryDate,
      stockProductId: item.stockProductId,
      isFirtsOut: false,
      saleId: filterItemsWithoutStockReservation?.[index]?.saleId
    }));
  };

  const getItemsList = () => {
    setItemsList(items);
  };

  /**
   * Actualiza la unidad en la posición indicada dentro de la lista de items.
   * Usa una actualización funcional del estado para que, cuando varias unidades
   * apliquen sus datos en el mismo ciclo de render (ej. al pre-rellenar el costo
   * y proveedor desde la cotización), cada llamada opere sobre la lista más
   * reciente y no se sobrescriban entre sí.
   * @param index Índice de la unidad dentro de itemsList.
   * @param product Datos actualizados de la unidad.
   */
  const updateItemWithinTheList = (index: number, product: IproductItemData) => {
    updateListOfProductsToUpdate?.(product);
    setItemsList(prevItemsList => {
      const newItemsList = [...prevItemsList];
      newItemsList[index] = product;
      return newItemsList;
    });
  };

  const sameProviderAndPricesInAll = () => {
    if (!enabledSameProviderAndPricesInAll) return;
    if (useSameProvider || useSameMPN) {
      setUseSameProvider(!useSameProvider);
      setUseSameMPN(!useSameMPN);
      return;
    }
    setHasDataChanged(true);
    setUseSameProvider(true);
    setUseSameMPN(true);
    const updatedItemsList = itemsList.map(item => {
      if (item.stockReservation !== null) return { ...item };
      const updatedItem = {
        ...item,
        provider: currentCommonProvider ?? undefined,
        providerId: currentCommonProviderId ?? undefined,
        purchaseValue: currentCommonBuyingPrice ?? undefined,
        providerPurchaseOrder: currentCommonProviderPurchaseOrder ?? undefined,
        mpn: currentCommonMPN ?? undefined,
        mpnId: currentCommonMPNId ?? undefined
      };
      updateListOfProductsToUpdate?.(updatedItem);
      return updatedItem;
    });
    setItemsList([...updatedItemsList]);
  };

  const handleChangeReservingAll = (checked: boolean) => {
    if (allItemsIsReserved) {
      setIsCancelReservationModalOpen(true);
    } else {
      setIsReservingAll(true);
      setIsBulkReservationModalOpen(true);
    }
  };

  const generatePillsCopy = () => {
    if (referencePills && Object.entries(referencePills) && Object.entries(referencePills).length > 0) {
      const list = Object.entries(referencePills);
      const newList = [];
      for (const item of list) {
        if (item[0] !== 'Gama' && item[0] !== 'Cargo' && item[0] !== 'Perfil') {
          let newItem = {
            key: item[0],
            value: item[1]
          };
          newList.push(newItem);
        }
      }
      if (newList && newList.length > 0) {
        let stringPills = '';

        for (const item of newList) {
          stringPills = stringPills + `${item.key}: ${item.value}\n`;
        }

        return stringPills;
      }
      return null;
    }
    return null;
  };

  const copyReferenceDetail = async () => {
    displaySuccessNotification({
      customJSXMessage: <>Información del producto copiada con éxito</>,
      duration: 3000
    });
    const pillsList = generatePillsCopy();
    const myText = `Nombre: ${productName}\n${
      pillsList || ''
    }SKU: ${sku}\nUnidades: ${itemsQuantity}\nPrecio por unidad: $${unitPrice}\nPrecio total: $${totalCost}`;
    await navigator.clipboard.writeText(myText);
  };

  const handleCheckReferenceButton = () => {
    if (!reference?.items?.length) return;
    if (productsSelected?.sku === reference?.sku && productsSelected?.items?.length === reference?.items?.length) {
      setProductsSelected(null);
    } else {
      setProductsSelected({ sku: reference?.sku, items: reference?.items });
    }
  };

  const handleToggleAccordion = () => setAccordionExpanded(sku, !isAccordionExpanded);

  /**
   * Sincroniza el estado del acordeón en el estado global cuando el SKU cambia
   * Asegura que si el acordeón está expandido, se mantenga en el estado global
   */
  const syncAccordionState = () => {
    if (isAccordionExpanded && !expandedAccordions[sku]) {
      setAccordionExpanded(sku, true);
    }
  };

  const redirectToQuoteDetail = () => {
    window.open(`/quote/details/${originalAttributes?.quoteId}`, '_blank');
  };

  useEffect(() => {
    getItemsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    syncAccordionState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sku]);

  return (
    <div className="referenceCard">
      <div className="referenceHeaderContainer">
        <div>
          <div className="nameBrandContainer">
            <NudosSelectButton
              iconName={
                productsSelected?.sku === reference.sku &&
                productsSelected?.items?.length === (reference.items?.length ?? 0)
                  ? 'checkmark'
                  : ''
              }
              isButtonActive={
                productsSelected?.sku === reference.sku &&
                productsSelected?.items?.length === (reference.items?.length ?? 0)
              }
              handleClickButton={handleCheckReferenceButton}
              componentSize="xs w-8 h-8 mr-8"
            />
            {originalAttributes?.quoteId && (
              <div onClick={redirectToQuoteDetail}>
                <BordBadge
                  size="small"
                  className="quoteChip"
                  label={
                    <div className="quoteChipContainer">
                      <span>Cotización</span>
                      <div className="divider"></div>
                      <div className="flex items-center gap-4">
                        <span>N° {originalAttributes?.quoteId}</span>
                        <BordOneToneIcon variant="arrowRight" standardSize={12} className="arrowRightIcon" />
                      </div>
                    </div>
                  }
                  variant="gradient"
                />
              </div>
            )}
            <div className="nameAndBrand">
              <div className="commercialName">
                <div className="falseSpace" />
                <span className="font-bold">{productName}</span>
                <div className="brandImg">
                  <div className="boxBrandImg">
                    <img alt="brandImg" className="imgStyle" src={brandImage} />
                  </div>
                </div>
              </div>
            </div>
            {isDepCategory && (
              <div className="categoryBadge">
                <BordOneToneIcon variant="laptopSettings" standardSize={14} stroke="#4096F0" />
                DEP
              </div>
            )}
            <div className="cursor-pointer" onClick={copyReferenceDetail}>
              <BordOneToneIcon variant="copy" standardSize={18} stroke="#070F21" />
            </div>
          </div>
          <div className="accordionToggleButton" onClick={handleToggleAccordion}>
            <span className="accordionToggleText">{isAccordionExpanded ? 'Ocultar unidades' : 'Ver unidades'}</span>
            <BordOneToneIcon
              variant="arrowHeadDown"
              standardSize={16}
              className={`accordionArrow ${isAccordionExpanded ? 'accordionArrowRotated' : ''}`}
            />
          </div>
          <div className="skuContainer">
            <div>SKU Catalogo:</div>
            <div>{sku || 'Sin datos'}</div>
          </div>
          <div className="referenceHeader">
            <div className="units">
              <div className="titleValue">Unid. solicitado</div>
              <div className="defaultValue">{itemsQuantity}</div>
            </div>
            <div className="boxPills">
              <div className="dividerStyle" />
              <div className="pillsContainer">
                {Object.entries(referencePills).map(([key, val]) => {
                  if (key !== 'Gama' && key !== 'Cargo' && !key.includes('Perfil') && val) {
                    return (
                      <div key={uuid()} className="pillContainer">
                        <div className="pillTitle">{key}</div>
                        <div className="pillValue">{val}</div>
                      </div>
                    );
                  }
                  return null;
                })}
                {renderEmptyPillsMessage && (
                  <div className="pillContainer">
                    <div className="pillTitle">Pills</div>
                    <div className="pillValue">Sin datos</div>
                  </div>
                )}
              </div>
              <div className="dividerStyle" />
            </div>
            <div className="unitCost">
              <div className="titleValue">c/u</div>
              <div className="defaultValue">
                {formatCost(+unitPrice, 2)} {currencyText}
              </div>
            </div>
            <div className="totalCost ml-24">
              <div className="titleValue">Total</div>
              <div className="defaultValue">
                {formatCost(+totalCost, 2)} {currencyText}
              </div>
            </div>
            {originalAttributes?.etaMin && originalAttributes?.etaMax && (
              <div className="totalCost ml-24">
                <div className="titleValue">Fecha de entrega</div>
                <div className="defaultValue">
                  {`${originalAttributes.etaMin} - ${originalAttributes.etaMax} semanas`}
                </div>
              </div>
            )}
          </div>
        </div>
        <StockMetrics
          currentStockCount={currentStockCount}
          stockAvailableReservationCount={stockAvailableReservationCount}
          reservedStockCount={reservedStockCount}
          reservedStockInfo={reservedStockInfo ?? { quantity: 0, items: [] }}
          unitsCount={itemsQuantity}
        />
      </div>
      <div className="informationContainer">
        <div className="informationHeader">
          {referenceHasMoreThanOneItem && (
            <div className="rightSection">
              <div
                className={`iconContainer ${!enabledSameProviderAndPricesInAll ? 'disabledCheckbox' : ''}`}
                onClick={sameProviderAndPricesInAll}
              >
                <CheckMark role={useSameProvider ? 'active' : ''} />
              </div>
              <div className={`textContainer ${!enabledSameProviderAndPricesInAll ? 'disabledText' : ''}`}>
                Usar los mismos datos (costo, proveedor, n° de orden y MPN) en todas las unidades no vinculadas a Stock.
              </div>
            </div>
          )}
          {referenceHasMoreThanOneItem && <div className="dividerStyle min" />}
          <div className="flex items-center gap-8">
            <div className="text-xss text-nudos-gray-500">Reservar todas las unidades</div>
            <div id="customSwitch">
              <Switch
                checked={isReservingAll || allItemsIsReserved}
                onChange={handleChangeReservingAll}
                style={{ width: 'max-content', height: '24px' }}
                loading={false}
                disabled={disabledSwitch}
              />
            </div>
          </div>
        </div>
        <BordAcordeon isExpanded={isAccordionExpanded}>
          <div className="referenceProductItemsList">
            <div className="w-full">
              {itemsList?.map((item, i) => {
                return (
                  <ProductInformationCard
                    key={`product-InformationCard-${item.saleId}-${i}`}
                    originalAttributes={originalAttributes}
                    updateItemWithinTheList={productData => updateItemWithinTheList(i, productData)}
                    productItem={item}
                    useSameProvider={useSameProvider}
                    useSameMPN={useSameMPN}
                    setHasDataChanged={setHasDataChanged}
                    isFirstItem={i === 0}
                    currentCommonProvider={currentCommonProvider || ''}
                    currentCommonBuyingPrice={String(currentCommonBuyingPrice || '')}
                    countryId={countryId}
                    countryCode={countryCode}
                    serialErrorsList={serialErrorsList}
                    changeErrorList={changeErrorList}
                    organizationName={organizationName}
                    updateWithoutLoaders={updateWithoutLoaders}
                    productName={productName}
                    savedOrganizationConfigs={savedOrganizationConfigs}
                    organizationId={organizationId}
                    orderId={orderId}
                    sku={sku}
                    stockAvailableReservationCount={stockAvailableReservationCount}
                    stockReservation={item?.stockReservation ?? null}
                    orderStatus={orderStatus}
                    productBrand={reference?.brand || ''}
                    isDelivered={!!getProductsBySku?.find(product => product.serial === item.serial)}
                  />
                );
              })}
            </div>
          </div>
        </BordAcordeon>
      </div>

      {isBulkReservationModalOpen && (
        <BulkSelectSerialModal
          isOpen={isBulkReservationModalOpen}
          onClose={() => {
            setIsBulkReservationModalOpen(false);
            setIsReservingAll(false);
          }}
          items={mapAvailableItemsToFloatSelectSerialItems(
            stockProductAvailabilityData?.find(item => item.sku === reference.sku)?.availableStockInfo?.items
          )}
          onSuccess={async () => {
            setIsBulkReservationModalOpen(false);
            setIsReservingAll(false);
            setAccordionExpanded(sku, true);
            await updateWithoutLoaders();
          }}
          requiredUnits={allItemsWithoutStockReservation}
        />
      )}
      {isCancelReservationModalOpen && (
        <CancelReservationModal
          isOpen={isCancelReservationModalOpen}
          onClose={() => setIsCancelReservationModalOpen(false)}
          onSuccess={async () => {
            setAccordionExpanded(sku, true);
            await updateWithoutLoaders();
          }}
          items={mapAvailableItemsToFloatSelectSerialItems(
            renderStockProducts.filter(
              product =>
                !getProductsBySku?.some(
                  deliveredProduct => deliveredProduct.serial === product.serial && deliveredProduct.sku === sku
                )
            )
          )}
          orderId={orderId}
          stockProductsIds={renderStockProducts
            .filter(
              product =>
                !getProductsBySku?.some(
                  deliveredProduct => deliveredProduct.serial === product.serial && deliveredProduct.sku === sku
                )
            )
            .map(item => item.stockProductId)}
        />
      )}
    </div>
  );
};

interface IReferenceCard {
  reference: IproductReferenceDataInCountry;
  setHasDataChanged: React.Dispatch<React.SetStateAction<boolean>>;
  countryId?: number | null;
  countryCode: string;
  serialErrorsList?: ISerialErrors[];
  changeErrorList?: (items: ISerialErrors[]) => void;
  organizationName?: string;
  updateListOfProductsToUpdate: (product: IproductItemData) => void;
  updateWithoutLoaders: () => Promise<void>;
  organizationId?: number;
  orderId: string;
  savedOrganizationConfigs: IOrganizationConfig[];
  orderStatus?: string;
  shimpmentData?: IShipmentData[];
}

export default ReferenceCard;
